import React, { useState, useRef, useEffect, useContext, useLayoutEffect } from 'react';
import { Box, Typography, Button, Chip, Stack, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import CustomizedButtonDatePicker from './CustomizedButtonDatePicker';
import CustomizedButtonWeekPicker from './CustomizedButtonWeekPicker';
import { generateDaySummarize, getTimesheetCountBasedOnStartDateAndEndDate } from '../../../../services/AiService';
import { toast } from 'sonner';
import CommonUtil from '../../../../util/CommonUtils';
import { getSubscriptionLimit } from '../../../../services/Subscription';
import CopyAndExport from './CopyAndExport';
import UpgradePopup from '../../../../components/subscription-component/UpgradePopup';
import RestrictPermissionPopup from '../../../../components/subscription-component/RestrictPermissionPopup';
import { useNavigate } from 'react-router-dom';
import { sidebarContext } from '../../../../util/context';
import { Divider } from 'antd';
import AppContext from "../../../../../src/config/AppContext";
import { getUserPreferences, resetUserPreferences, updateUserPreferences } from '../../../../services/DashboardService';
import "../SummaryAgent.scss"

const Chat = ({ isDateSelected, isRefreshed, setRefreshed }) => {

    const { t } = useTranslation();
    const customDatePickerRef = useRef();
    const datePickerRef = useRef();
    const weekPickerRef = useRef();
    const [openPickerType, setOpenPickerType] = useState(null);
    const redatePickerRef = useRef();
    const reweekPickerRef = useRef();
    const { mode } = useContext(AppContext);
    const [userPreferences, setUserPreferences] = useState({});
    const [hasActivityData, setHasActivityData] = useState(true);
    const [chatSteps, setChatSteps] = useState([]);
    const [userPrefTimeSelection, SetUserPrefTimeSelection] = useState("");
    const [upgradeFromat, setUpgradeFromat] = useState(false)
    const [savedprefrence, setSavedprefrence] = useState(false)
    const [resetTimeValue, setResetTimeValue] = useState(false)
    const userId = CommonUtil.getLoggedUser()?.userId;
    const [selections, setSelections] = useState({
        language: '',
        timePeriod: '',
        style: '',
        length: '',
    });


    const chatEndRef = useRef(null);
    const summaryRef = useRef({});
    const navigate = useNavigate();
    const { subscriptionDetails } = useContext(sidebarContext);
    const [activeChange, setActiveChange] = useState('');
    const [isGenerated, setIsGenerated] = useState(false);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [disableAll, setDisableAll] = useState(false);
    const [canStartNewFlow, setCanStartNewFlow] = useState(false);
    const [typedText, setTypedText] = useState("");
    const typeIndexRef = useRef(0);
    const intervalRef = useRef(null);

    const languageOptions = [t("RAL0028"), t("RAL0648"), t("RAL0649"), t("RAL0683"), t("RAL0750")];
    const timePeriodOptions = [t("RAL0050"), t("This Week"), t("RAL0837"), t("Custom Week")];
    const styleOptions = [t("RAL0678"), t("RAL0679"), t("RAL0680")];
    const lengthOptions = [t('150 words'), t('300 words'), t('400 words'), t('500 words'), t('600 words')];

    const defaultChatStep = {
        id: 'start', type: 'question',
        message: t('Hi there I\'m your AI Summary Assistant.\nI\'ll help you summarize your tracked activity in seconds.\n\nReady to begin?'),
        options: [{ label: 'Get Started', action: 'start' }], disabled: false, locked: false
    }

    const userPrefChatStep = {
        id: 'start-with-already-prefrence', type: 'user-prefrence',
        message: ``,
        options: timePeriodOptions.map((period) => ({ label: period, action: 'selectTimePeriod', value: period })),
    }

    useEffect(() => {
        getUserPreferences(userId)
            .then((response) => {
                const { timeRange, wordCount, style, aiLanguage } = response.data.data
                if (response.data && response.data.data) {
                    setUserPreferences(response.data.data);
                    if (timeRange && wordCount && style && aiLanguage) {
                        setChatSteps([userPrefChatStep]);
                        setSavedprefrence(true)
                        setSelections({
                            language: aiLanguage,
                            style: style,
                            length: wordCount,
                        })
                    } else {
                        setChatSteps([defaultChatStep]);
                    }
                }
            })
            .catch((error) => {
                console.log("Error fetching user preferences: " + error.message);
            });
    }, [])

    const handleOpenDatePicker = (e) => {
        setOpenPickerType('date');
        if (weekPickerRef.current) {
            weekPickerRef.current.closePicker();
        }
        if (datePickerRef.current) {
            datePickerRef.current.openPicker(e.currentTarget);
        }
    };

    const handleOpenWeekPicker = (e) => {
        setOpenPickerType('week');
        if (datePickerRef.current) {
            datePickerRef.current.closePicker();
        }
        if (weekPickerRef.current) {
            weekPickerRef.current.openPicker(e.currentTarget);
        }
    };

    const handleReOpenDatePicker = (e) => {
        if (redatePickerRef.current) {
            redatePickerRef.current.openPicker(e.currentTarget);
        }
    };

    const handleReOpenWeekPicker = (e) => {
        if (reweekPickerRef.current) {
            reweekPickerRef.current.openPicker(e.currentTarget);
        }
    };

    const handleWeekSelection = (weekRange) => {
        if (!weekRange || !weekRange.startDate || !weekRange.endDate) return;

        const formattedStartDate = dayjs(weekRange.startDate).format('DD-MM-YYYY');
        const formattedEndDate = dayjs(weekRange.endDate).format('DD-MM-YYYY');

        const formattedWeek = `${formattedStartDate} to ${formattedEndDate}`;

        setSelections(prev => ({
            ...prev,
            timePeriod: `${formattedStartDate} to ${formattedEndDate}`
        }));

        handleAction('selectTimePeriod', formattedWeek);
    };

    const handleReWeekSelection = (weekRange) => {
        if (!weekRange || !weekRange.startDate || !weekRange.endDate) return;

        const formattedStartDate = dayjs(weekRange.startDate).format('DD-MM-YYYY');
        const formattedEndDate = dayjs(weekRange.endDate).format('DD-MM-YYYY');

        const formattedWeek = `${formattedStartDate} to ${formattedEndDate}`;

        setSelections(prev => ({
            ...prev,
            timePeriod: `${formattedStartDate} to ${formattedEndDate}`
        }));

        handleAction('reselectTimePeriod', formattedWeek);
    };

    const handleDateSelection = (newDate) => {
        if (dayjs(newDate).isValid()) {
            const formattedDate = dayjs(newDate).format('DD-MM-YYYY');
            setSelections(prev => ({
                ...prev,
                timePeriod: formattedDate
            }));
            handleAction('selectTimePeriod', `${formattedDate}`);
        }
    };

    const handleReDateSelection = (newDate) => {
        const formattedDate = dayjs(newDate).format('DD-MM-YYYY');
        setSelections(prev => ({
            ...prev,
            timePeriod: formattedDate
        }));
        handleAction('reselectTimePeriod', `${formattedDate}`);
    };

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatSteps]);

    // useEffect(() => {
    //     const lastStep = chatSteps[chatSteps.length - 1];
    //     if (!lastStep || lastStep.type !== 'generatedSummary') return;
    //     console.log(lastStep);
    //     const ref = summaryRef.current[lastStep.id];
    //     // if (!ref) return;

    //     let currentIndex = 0;
    //     let currentText = "";
    //     const summary = lastStep.summary;

    //     setIsSummaryTyped(false);

    //     const interval = setInterval(() => {
    //         if (currentIndex < summary.length) {
    //             currentText += summary[currentIndex];
    //             ref.innerHTML = currentText;
    //             currentIndex++;

    //             if (chatEndRef.current) {
    //                 chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    //             }
    //         } else {
    //             clearInterval(interval);
    //             setIsSummaryTyped(true);
    //             setTimeout(() => {
    //                 addStep({
    //                     id: `restart-${Date.now()}`,
    //                     type: "question",
    //                     message: t("RAL1086"),
    //                     options: [{ label: "Get Started", action: "start" }],
    //                     disabled: false,
    //                 });
    //                 setCanStartNewFlow(true);
    //             }, 1000);
    //         }
    //     }, 15);

    //     return () => clearInterval(interval);
    // }, [chatSteps]);

    useEffect(() => {
        const lastStep = chatSteps[chatSteps.length - 1];
        if (!lastStep || lastStep.type !== "generatedSummary") return;

        const ref = summaryRef.current[lastStep.id];
        if (!ref) return;

        const summary = lastStep.summary;
        setIsSummaryTyped(false);

        let startTime = Date.now();

        function update() {
            const elapsed = Date.now() - startTime;
            // Calculate how many characters should be shown based on elapsed time
            const charsToShow = Math.min(Math.floor(elapsed / 15), summary.length);
            ref.innerHTML = summary.slice(0, charsToShow);

            if (chatEndRef.current) {
                chatEndRef.current.scrollIntoView({ behavior: "smooth" });
            }

            if (charsToShow < summary.length) {
                requestAnimationFrame(update);
            } else {
                setIsSummaryTyped(true);
                setTimeout(() => {
                    addStep({
                        id: `restart-${Date.now()}`,
                        type: "question",
                        message: t("RAL1086"),
                        options: [{ label: "Get Started", action: "start" }],
                        disabled: false,
                    });
                    setCanStartNewFlow(true);
                }, 1000);
            }
        }
        requestAnimationFrame(update);

        // Cleanup if chatSteps change during animation
        return () => {
            setIsSummaryTyped(true);
        };
    }, [chatSteps]);


    const addStep = (newStep) => {
        setChatSteps((prev) => [...prev, { ...newStep, locked: false }]);
    };

    const lockPreviousSteps = () => {
        setChatSteps((prev) => prev.map(step => ({ ...step, locked: true })));
    };

    const unlockPreviousSteps = () => {
        setChatSteps((prev) => prev.map(step => ({ ...step, locked: false })));
    };

    const disableLastOptions = () => {
        setChatSteps((prev) => {
            const updated = [...prev];
            const lastIndex = updated.length - 1;
            if (updated[lastIndex]?.options) {
                updated[lastIndex].disabled = true;
            }
            return updated;
        });
    };

    const enableLastOptions = () => {
        setChatSteps((prev) => {
            const updated = [...prev];
            const lastIndex = updated.length - 1;
            if (updated[lastIndex]?.options) {
                updated[lastIndex].disabled = false;
            }
            return updated;
        });
    };

    const getLengthOptions = (timePeriod) => {
        const base = [];
        const isSingleDay = timePeriod === 'Today' || (timePeriod.includes('-') && !timePeriod.includes('to'));
        const isWeek = timePeriod === 'This Week' || timePeriod.includes('to');

        if (isSingleDay) {
            base.push({ label: '150 words', action: 'selectLength', value: '150 words' });
        }

        base.push({ label: '300 words', action: 'selectLength', value: '300 words' });

        if (isWeek) {
            base.push(
                { label: '400 words', action: 'selectLength', value: '400 words' },
                { label: '500 words', action: 'selectLength', value: '500 words' },
                { label: '600 words', action: 'selectLength', value: '600 words' }
            );
        }

        return base;
    };

    const getReLengthOptions = (timePeriod) => {
        const base = [];

        if (timePeriod === 'Today' || (timePeriod.includes('-') && !timePeriod.includes('to'))) {
            base.push({ label: '150 words', action: 'reselectLength', value: '150 words' });
        }

        base.push({ label: '300 words', action: 'reselectLength', value: '300 words' });

        if (timePeriod === 'This Week' || timePeriod.includes('to')) {
            base.push(
                { label: '400 words', action: 'reselectLength', value: '400 words' },
                { label: '500 words', action: 'reselectLength', value: '500 words' },
                { label: '600 words', action: 'reselectLength', value: '600 words' }
            );
        }

        return base;
    };

    const handleAction = async (action, value = '') => {

        if ((disableAll && !(canStartNewFlow && action === 'start'))) return;

        if (action === 'start') {
            if (action !== 'selectTimePeriod' || (value !== 'Custom' && value !== 'Custom Week')) {
                if (datePickerRef.current) datePickerRef.current.closePicker();
                if (weekPickerRef.current) weekPickerRef.current.closePicker();
                setOpenPickerType(null);
            }
            SetUserPrefTimeSelection({ type: "user-prefrence-inactive", value: "", currentStatus: "activated" });
            setRestartStepAdded(false);
            disableLastOptions();
            setSavedprefrence(false);
            setSelections({ language: '', timePeriod: '', style: '', length: '' });
            setActiveChange('');
            setIsGenerated(false);
            setDisableAll(false);
            setCanStartNewFlow(false);
            addStep({
                id: `get-started-${Date.now()}`,
                type: 'user',
                message: `Get Started`
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            addStep({
                id: `language-${Date.now()}`,
                type: 'question',
                field: 'language',
                selected: '',
                message: t('What language would you like your summary in?'),
                options: languageOptions.map((lang) => ({ label: lang, action: 'selectLanguage', value: lang })),
                disabled: false
            });
        }

        if (action === 'user-prefrence') {

            SetUserPrefTimeSelection({ type: "user-prefrence-inactive", value: "", currentStatus: "activated" })
            setSelections({ language: '', timePeriod: '', style: '', length: '' });
            setSavedprefrence(false)
            addStep({
                id: `user-language-${Date.now()}`,
                type: 'user',
                message: `${value}`
            });
            resetUserPreferences(userId, {
                ...userPreferences,
                aiLanguage: null,
                style: null,
                wordCount: null,
                timeRange: null
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            addStep({
                id: `language-${Date.now()}`,
                type: 'question',
                field: 'language',
                selected: '',
                message: t('RAL1097'),
                options: languageOptions.map((lang) => ({ label: lang, action: 'selectLanguage', value: lang })),
                disabled: false
            });
        }

        if (action === 'selectLanguage') {
            disableLastOptions();
            setSelections((prev) => ({ ...prev, language: value, timePeriod: '', style: '', length: '' }));
            setActiveChange('language');
            waitForStepAndUpdate('language', value);
            addStep({
                id: `user-language-${Date.now()}`,
                type: 'user',
                message: `${value}`
            });

            await new Promise(resolve => setTimeout(resolve, 1000));
            addStep({
                id: `timePeriod-${Date.now()}`,
                type: 'question',
                selected: '',
                field: 'timePeriod',
                message: t('RAL1096'),
                options: timePeriodOptions.map((period) => ({ label: period, action: 'selectTimePeriod', value: period })),
                extraChange: { label: t('RAL0041'), action: 'popLanguage', target: 'language' },
                disabled: false
            });

            setResetTimeValue(!resetTimeValue);

        }

        if (action === 'reselectLanguage') {
            disableLastOptions();
            setSelections((prev) => ({ ...prev, language: value }));
            setActiveChange('language');
            waitForReStepAndUpdate('language', value);
            addStep({
                id: `user-language-${Date.now()}`,
                type: 'user',
                message: `${value}`
            });
            await new Promise(resolve => setTimeout(resolve, 1000));

            const snapshot = {
                language: value,
                timePeriod: selections.timePeriod,
                style: selections.style,
                length: selections.length,
            };

            addStep({
                id: `summary-${Date.now()}`,
                type: 'summary',
                summarySelections: snapshot,
            });

        }

        if (action === 'selectTimePeriod') {
            disableLastOptions();
            setSelections((prev) => ({ ...prev, timePeriod: value }));
            setActiveChange('timePeriod');
            waitForStepAndUpdate('timePeriod', value);
            addStep({
                id: `checking-activity-${Date.now()}`,
                type: 'loading',
                message: t('RAL1098')
            });

            const hasActivity = await checkActivityData(value);
            setHasActivityData(hasActivity);
            setChatSteps(prev => prev.filter(step => step.id !== `checking-activity-${Date.now()}`));
            if (!hasActivity) {
                addStep({
                    id: `user-timePeriod-${Date.now()}`,
                    type: 'user',
                    message: `${value}`
                });
                await new Promise(resolve => setTimeout(resolve, 1000));
                addStep({
                    id: `no-activity-${Date.now()}`,
                    type: 'noActivity',
                    message: t('RAL0038')
                });
                return;
            }

            if (value === 'Today') {
                if (savedprefrence) SetUserPrefTimeSelection({ type: "user-prefrence-active", value: t("RAL0050"), currentStatus: "activated" })
                const formattedDate = dayjs.tz(new Date(), CommonUtil.getTimeZoneId()).format('DD-MM-YYYY');
                addStep({
                    id: `user-timePeriod-${Date.now()}`,
                    type: 'user',
                    message: `${formattedDate}`
                });
            } else if (value === 'This Week') {
                if (savedprefrence) SetUserPrefTimeSelection({ type: "user-prefrence-active", value: "This Week", currentStatus: "activated" })
                const tzDate = dayjs.tz(new Date(), CommonUtil.getTimeZoneId());
                const startOfWeek = tzDate.day(0).startOf('day');
                const endOfWeek = tzDate.day(6).endOf('day');
                const formattedStartOfWeek = startOfWeek.format('DD-MM-YYYY');
                const formattedEndOfWeek = endOfWeek.format('DD-MM-YYYY');

                addStep({
                    id: `user-timePeriod-${Date.now()}`,
                    type: 'user',
                    message: `${formattedStartOfWeek} to ${formattedEndOfWeek}`
                });
            } else {
                if (savedprefrence) SetUserPrefTimeSelection({ type: "user-prefrence-active", value: value?.includes("to") ? "Custom Week" : t("RAL0837"), currentStatus: "activated" })
                addStep({
                    id: `user-timePeriod-${Date.now()}`,
                    type: 'user',
                    message: `${value}`
                });
            }

            const isSingleDay = value === 'Today' || (value.includes('-') && !value.includes('to'));
            // const savedLengthValid = !isSingleDay || (isSingleDay && userPreferences?.wordCount <= 300);
            const isWeek = value === 'This Week' || value.includes('to');
            const savedWordCount = parseInt(userPreferences.wordCount);
           const savedLengthValid = 
           (isSingleDay && savedWordCount <= 300) || 
           (isWeek && savedWordCount >= 300);

            if (savedprefrence) {
                setSelections(prev => ({
                    ...prev,
                    style: userPreferences.style,
                    language: userPreferences.aiLanguage
                }));

                if (savedLengthValid ) {
                    setSelections(prev => ({
                        ...prev,
                        length: userPreferences.wordCount.toString() + ' words'
                    }));
                    addStep({
                        id: `loader-${Date.now()}`,
                        type: 'loader',
                    });
                    handleGenerate(value);
                } else {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    addStep({
                        id: `length-${Date.now()}`,
                        type: 'question',
                        selected: '',
                        field: 'length',
                        message: t('RAL1091'),
                        options: getLengthOptions(value),
                        extraChange: { label: t('RAL1093'), action: 'popTimePeriod', target: 'timePeriod' },
                        disabled: false
                    });
                }
            } else {
                await new Promise(resolve => setTimeout(resolve, 1000));
                addStep({
                    id: `style-${Date.now()}`,
                    type: 'question',
                    selected: '',
                    field: 'style',
                    message: t('RAL1094'),
                    options: styleOptions.map((s) => ({ label: s, action: 'selectStyle', value: s })),
                    extraChange: { label: t('RAL1093'), action: 'popTimePeriod', target: 'timePeriod' },
                    disabled: false
                });
            }
        }

        if (action === 'reselectTimePeriod') {
            disableLastOptions();
            setSelections((prev) => ({ ...prev, timePeriod: value }));
            setActiveChange('timePeriod');
            waitForReStepAndUpdate('timePeriod', value);
            if (value === 'Today') {
                const formattedDate = dayjs.tz(new Date(), CommonUtil.getTimeZoneId()).format('DD-MM-YYYY');
                addStep({
                    id: `user-timePeriod-${Date.now()}`,
                    type: 'user',
                    message: `${formattedDate}`
                });
            } else if (value === 'This Week') {

                const tzDate = dayjs.tz(new Date(), CommonUtil.getTimeZoneId());
                const startOfWeek = tzDate.day(0).startOf('day');
                const endOfWeek = tzDate.day(6).endOf('day');
                const formattedStartOfWeek = startOfWeek.format('DD-MM-YYYY');
                const formattedEndOfWeek = endOfWeek.format('DD-MM-YYYY');

                addStep({
                    id: `user-timePeriod-${Date.now()}`,
                    type: 'user',
                    message: `${formattedStartOfWeek} to ${formattedEndOfWeek}`
                });
            } else {
                addStep({
                    id: `user-timePeriod-${Date.now()}`,
                    type: 'user',
                    message: `${value}`
                });
            }
            await new Promise(resolve => setTimeout(resolve, 1000));

            const snapshot = {
                language: selections.language,
                timePeriod: value,
                style: selections.style,
                length: selections.length,
            };

            const isValidSnapshot = Object.values(snapshot).every(val => val != null);
            var currLength = selections.length;
            let checker = await checkActivityData(value);
            if (!checker) {
                setHasActivityData(false);
                addStep({
                    id: `no-activity-${Date.now()}`,
                    type: 'noActivity',
                    message: t('RAL0038')
                });
            } else {
                var lengthArray = getReLengthOptions(value);
                const isValueInList = lengthArray.some(option => (
                    option.value === currLength || option.value.includes(currLength + "")
                ));

                if (isValueInList) {
                    addStep({
                        id: `summary-${Date.now()}`,
                        type: 'summary',
                        summarySelections: snapshot,
                    });
                } else {
                    let message = t('RAL1091');

                    addStep({
                        id: `change-${Date.now()}`,
                        type: 'requestion',
                        message,
                        field: 'length',
                        selected: '',
                        options: lengthArray,
                        disabled: false
                    });
                }
            }

        }

        if (action === 'selectStyle') {
            disableLastOptions();
            setSelections((prev) => ({ ...prev, style: value, length: '' }));
            setActiveChange('style');
            waitForStepAndUpdate('style', value);
            addStep({
                id: `user-style-${Date.now()}`,
                type: 'user',
                message: `${value}`
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            addStep({
                id: `length-${Date.now()}`,
                type: 'question',
                selected: '',
                field: 'length',
                message: t('RAL1091'),
                options: getLengthOptions(selections.timePeriod),
                extraChange: { label: t('RAL1092'), action: 'popStyle', target: 'style' },
                disabled: false
            });
        }

        if (action === 'reselectStyle') {
            disableLastOptions();
            setSelections((prev) => ({ ...prev, style: value }));
            setActiveChange('style');
            waitForReStepAndUpdate('style', value);
            addStep({
                id: `user-style-${Date.now()}`,
                type: 'user',
                message: `${value}`
            });
            await new Promise(resolve => setTimeout(resolve, 1000));
            const snapshot = {
                language: selections.language,
                timePeriod: selections.timePeriod,
                style: value,
                length: selections.length,
            };

            addStep({
                id: `summary-${Date.now()}`,
                type: 'summary',
                summarySelections: snapshot,
            });
        }

        if (action === 'selectLength') {
            disableLastOptions();
            setSelections({ ...selections, length: value?.includes("words") ? parseInt(value?.split(" ")[0]) : value });
            setActiveChange('length');
            waitForStepAndUpdate('length', value);
            addStep({
                id: `user-length-${Date.now()}`,
                type: 'user',
                message: `${value}`
            });

            if (savedprefrence) {
                addStep({
                    id: `loader-${Date.now()}`,
                    type: 'loader',
                });
                handleGenerate(value);

            } else {
                await new Promise(resolve => setTimeout(resolve, 1000));
                const snapshot = {
                    language: selections.language,
                    timePeriod: selections.timePeriod,
                    style: selections.style,
                    length: value,
                };
                addStep({
                    id: `summary-${Date.now()}`,
                    type: 'summary',
                    summarySelections: snapshot,
                });
            }
        }

        if (action === 'reselectLength') {
            disableLastOptions();
            setSelections((prev) => ({ ...prev, length: value }));
            setActiveChange('length');
            waitForReStepAndUpdate('length', value);
            addStep({
                id: `user-length-${Date.now()}`,
                type: 'user',
                message: `${value}`
            });

            const snapshot = {
                language: selections.language,
                timePeriod: selections.timePeriod,
                style: selections.style,
                length: value,
            };

            addStep({
                id: `summary-${Date.now()}`,
                type: 'summary',
                summarySelections: snapshot,
            });
        }


        if (action.startsWith("pop")) {
            const field = action.replace('pop', '').toLowerCase();

            if (field === 'timeperiod') {
                setSelections((prev) => ({
                    ...prev,
                    timePeriod: '',
                    style: '',
                    length: ''
                }));
                const timePeriodStepIndex = chatSteps.findIndex(step =>
                    step.type === 'question' &&
                    step.message === t('RAL1096')
                );
                // if (timePeriodStepIndex >= 0) {
                //     const updatedSteps = chatSteps.slice(0, timePeriodStepIndex + 1);
                //     const filteredSteps = updatedSteps.filter(step => step.type !== 'noActivity');
                //     setChatSteps(filteredSteps);
                //     enableLastOptions();
                //     unlockPreviousSteps();
                //     return;
                // }

                if (userPrefTimeSelection.type === "user-prefrence-active") {

                    SetUserPrefTimeSelection({ type: "user-prefrence-inactive", value: "", currentStatus: "chosen" });
                    setChatSteps([userPrefChatStep]);
                    setUpgradeFromat(false);
                    return;
                }
            } else {
                setSelections((prev) => ({
                    ...prev,
                    [field]: ''
                }));

            }

            // let finalUpdatedSteps = [];

            // setChatSteps((prev) => {
            //     const updated = [...prev];
            //     let count = 0;
            //     let index = updated.length - 1;

            //     while (index >= 0 && count < 3) {
            //         const step = updated[index];
            //         if (step.type === 'question' || step.type === 'requestion' || step.type === 'user' || step.type === 'noActivity') {
            //             updated.splice(index, 1);
            //             count++;
            //         }
            //         index--;
            //     }
            //     finalUpdatedSteps = updated;
            //     return updated;
            // });

            const updatedSteps = [...chatSteps];
            let count = 0;
            let index = updatedSteps.length - 1;

            while (index >= 0 && count < 3) {
                const step = updatedSteps[index];
                if (
                    step.type === 'question' ||
                    step.type === 'requestion' ||
                    step.type === 'user' ||
                    step.type === 'noActivity'
                ) {
                    updatedSteps.splice(index, 1);
                    count++;
                }
                index--;
            }

            enableLastOptions();
            unlockPreviousSteps();
            setChatSteps(updatedSteps);
            const lastStep = updatedSteps[updatedSteps.length - 1];
            const isLastSummary = lastStep?.type === 'summary';
            if (isLastSummary) {

                let optionsList = timePeriodOptions.map((period) => ({ label: period, action: 'reselectTimePeriod', value: period }));
                let message = t('RAL1089');
                let fieldValue = 'timePeriod';
                let extra = {};

                addStep({
                    id: `change-${Date.now()}`,
                    type: 'requestion',
                    field: fieldValue,
                    options: optionsList,
                    message,
                    selected: '',
                    disabled: false,
                    extraChange: extra
                });

            } else {
                let optionsList = [];
                let message = '';
                let fieldValue = '';
                let extra = {};

                if (field === 'language') {
                    optionsList = languageOptions.map((lang) => ({ label: lang, action: 'selectLanguage', value: lang }));
                    message = t('RAL1097');
                    fieldValue = 'language';
                } else if (field === 'timeperiod') {
                    setActiveChange('language');
                    fieldValue = 'timePeriod';
                    optionsList = timePeriodOptions.map((period) => ({ label: period, action: 'selectTimePeriod', value: period }));
                    message = t('RAL1096');
                    extra = { label: t('Change language'), action: 'popLanguage', target: 'language' };
                } else if (field === 'style') {
                    setActiveChange('timePeriod');
                    fieldValue = 'style';
                    optionsList = styleOptions.map((s) => ({ label: s, action: 'selectStyle', value: s }));
                    message = t('RAL1094');
                    extra = { label: t('RAL1093'), action: 'popTimePeriod', target: 'timePeriod' };
                } else if (field === 'length') {
                    setActiveChange('style');
                    fieldValue = 'length';
                    optionsList = getLengthOptions(selections.timePeriod);
                    message = t('RAL1091');
                    extra = { label: t('RAL1092'), action: 'popStyle', target: 'style' };
                }

                addStep({
                    id: `change-${Date.now()}`,
                    type: 'question',
                    field: fieldValue,
                    message,
                    options: optionsList,
                    selected: '',
                    disabled: false,
                    extraChange: extra
                });
            }


        }

        if (action.startsWith('change')) {
            const field = action.replace('change', '').toLowerCase();
            let optionsList = [];
            let message = '';
            let fieldValue = '';

            lockPreviousSteps();

            if (field === 'language') {
                fieldValue = 'language';
                optionsList = languageOptions.map((lang) => ({ label: lang, action: 'reselectLanguage', value: lang }));
                message = t('RAL1090');
            }
            if (field === 'timeperiod') {
                fieldValue = 'timePeriod';
                optionsList = timePeriodOptions.map((period) => ({ label: period, action: 'reselectTimePeriod', value: period }));
                message = t('RAL1089');
            }
            if (field === 'style') {
                fieldValue = 'style';
                optionsList = styleOptions.map((s) => ({ label: s, action: 'reselectStyle', value: s }));
                message = t('RAL1088');
            }
            if (field === 'length') {
                fieldValue = 'length';
                optionsList = getReLengthOptions(selections.timePeriod);
                message = t('RAL1087');
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
            addStep({
                id: `change-${Date.now()}`,
                type: 'requestion',
                message,
                field: fieldValue,
                selected: '',
                options: optionsList,
                disabled: false
            });

        }
    };

    const waitForStepAndUpdate = (field, selectedValue, retries = 10) => {
        let attempts = 0;
        const interval = setInterval(() => {
            setChatSteps(prev => {
                const updated = [...prev];
                const index = updated.findLastIndex(
                    step =>
                        step.type === 'question' &&
                        step.field === field &&
                        (step.selected === '' || step.selected === undefined)
                );

                if (index !== -1) {
                    clearInterval(interval);
                    updated[index] = {
                        ...updated[index],
                        selected: selectedValue,
                    };
                } else if (++attempts >= retries) {
                    clearInterval(interval);
                    // console.warn(`Step for '${field}' not found after ${retries} tries.`);
                }

                return updated;
            });
        }, 100);
    };

    const waitForReStepAndUpdate = (field, selectedValue, retries = 10) => {
        let attempts = 0;
        const interval = setInterval(() => {
            setChatSteps(prev => {
                const updated = [...prev];
                const index = updated.findLastIndex(
                    step =>
                        step.type === 'requestion' &&
                        step.field === field &&
                        (step.selected === '' || step.selected === undefined)
                );

                if (index !== -1) {
                    clearInterval(interval);
                    updated[index] = {
                        ...updated[index],
                        selected: selectedValue,
                    };
                } else if (++attempts >= retries) {
                    clearInterval(interval);
                    // console.warn(`Step for '${field}' not found after ${retries} tries.`);
                }

                return updated;
            });
        }, 100);
    };


    const [isLimitLoading, setIsLimitLoading] = useState(true);
    const featureCode = "DAILY_AI_SUMMARIZATION_LIMITS";

    useEffect(() => {
        const fetchFeatureLimitDetails = async () => {
            try {
                const featureCode = "DAILY_AI_SUMMARIZATION_LIMITS";
                const res = await getSubscriptionLimit(featureCode);
                if (res?.data?.code === "RCI0000") {
                    setAiLimit(res?.data?.data?.limit || 0);
                    setCurrentAiLimit(res?.data?.data?.currValue || 0);
                } else if (res?.data?.code === "RCE0225") {
                    if (!["OWNER", "MANAGER"].includes(CommonUtil.getRoleCode())) {
                        setRestrictPopupOpen(true);
                    } else {
                        setUpgradePopupOpen(true);
                    }
                }
            } catch (error) {
                console.error("Error fetching user details:", error.message);
            } finally {
                setIsLimitLoading(false);
            }
        };
        fetchFeatureLimitDetails();
    }, []);

    const [generatedSummary, setGenerateSummary] = useState("");
    const [currentAiLimit, setCurrentAiLimit] = useState(0);
    const [dateValue, setDateValue] = useState(null);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [isTypewriterActive, setIsTypewriterActive] = useState(false);
    const [aiLimit, setAiLimit] = useState(0);
    const remainingAttempts = (aiLimit - currentAiLimit) < 0 ? 0 : aiLimit - currentAiLimit;
    const hasReachedLimit = remainingAttempts <= 0;
    const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);
    const [restrictPopupOpen, setRestrictPopupOpen] = useState(false);
    const [restartStepAdded, setRestartStepAdded] = useState(false);
    const [isSummaryTyped, setIsSummaryTyped] = useState(false);

    const checkActivityData = async (timePeriod) => {
        let calculatedStartDate, calculatedEndDate;

        if (timePeriod === "Today") {
            const tzDate = dayjs.tz(new Date(), CommonUtil.getTimeZoneId());
            calculatedStartDate = tzDate.startOf("day");
            calculatedEndDate = tzDate.endOf("day");
        } else if (timePeriod === "This Week") {
            const now = dayjs.tz(new Date(), CommonUtil.getTimeZoneId());
            calculatedStartDate = now.startOf("week").startOf("day");
            calculatedEndDate = now.endOf("week").endOf("day");
        } else if (timePeriod.includes('to')) {
            const [start, end] = timePeriod.split(' to ');
            calculatedStartDate = dayjs(start, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).startOf("day");
            calculatedEndDate = dayjs(end, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).endOf("day");
        } else {
            calculatedStartDate = dayjs(timePeriod, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).startOf("day");
            calculatedEndDate = dayjs(timePeriod, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).endOf("day");
        }

        if (!calculatedStartDate?.isValid() || !calculatedEndDate?.isValid()) {
            console.error('Invalid date range:', { calculatedStartDate, calculatedEndDate });
            return false;
        }

        try {
            const payload = {
                startTime: calculatedStartDate.utc().format(),
                endTime: calculatedEndDate.utc().format(),
            };

            const res = await getTimesheetCountBasedOnStartDateAndEndDate(payload);
            // console.log('Activity check response:', res);

            if (res?.data?.data?.timesheets > 0) {
                return true;
            }
            else {
                return false;
            }

        } catch (error) {
            console.error("Error checking activity data:", error);
            return true;
        }
    };

    function processTextSummary(text) {
        if (!text) return '';

        if (/<[a-z][\s\S]*>/i.test(text)) {
            if (text.includes('<body>')) {
                const bodyMatch = text.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                if (bodyMatch && bodyMatch[1]) {
                    return bodyMatch[1];
                }
            }
            return text;
        }

        const summary = text?.replace(/\"/g, "").trim();
        const startIndex = summary.indexOf("Projects:");
        const cleanSummary = startIndex >= 0 ? summary.substring(startIndex).trim() : summary;
        const lines = cleanSummary.split("\n");

        let htmlSummary = '<div class="summary">';
        lines.forEach((line) => {
            if (line.includes(":")) {
                const [label, content] = line.split(":", 2);
                htmlSummary += `<p><strong>${label.trim()}:</strong> ${content.trim()}</p>`;
            } else {
                htmlSummary += `<p>${line.trim()}</p>`;
            }
        });
        htmlSummary += "</div>";
        return htmlSummary;
    }

    const buildPayload = (selections) => {
        const languageMap = {
            English: 'en',
            Chinese: 'zh',
            Japanese: 'ja',
            Korean: 'ko',
            Spanish: 'es'
        };

        return {
            language: languageMap[selections.language] || 'en',
            wordCount: parseInt(selections.length),
            style: selections.style.toLowerCase()
        };
    };


    const handleGenerate = (value = '') => {
        setIsGenerated(false);

        setLoadingSummary(true);

        lockPreviousSteps();

        if (hasReachedLimit) {
            if (!["OWNER", "MANAGER"].includes(CommonUtil.getRoleCode())) {
                setRestrictPopupOpen(true);
                setGenerateSummary(undefined);
                setLoadingSummary(false);
                return;
            } else {
                setUpgradePopupOpen(true);
                setGenerateSummary(undefined);
                setLoadingSummary(false);
                return;
            }
        }

        setDisableAll(true);
        let calculatedStartDate, calculatedEndDate;

        if (selections.timePeriod === "Today" || value === "Today") {
            const tzDate = dayjs.tz(new Date(), CommonUtil.getTimeZoneId());
            calculatedStartDate = tzDate.startOf("day");
            calculatedEndDate = tzDate.endOf("day");
        } else if (selections?.timePeriod === "This Week" || value === "This Week") {
            const now = dayjs.tz(new Date(), CommonUtil.getTimeZoneId());

            calculatedStartDate = now.startOf("week").startOf("day");
            calculatedEndDate = now.endOf("week").endOf("day");
        } else if (selections?.timePeriod?.includes('to')) {

            const [start, end] = selections?.timePeriod?.split(' to ');
            calculatedStartDate = dayjs(start, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).startOf("day");
            calculatedEndDate = dayjs(end, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).endOf("day");

        } else if (value && value?.includes('to')) {

            const [start, end] = value?.split(' to ');
            calculatedStartDate = dayjs(start, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).startOf("day");
            calculatedEndDate = dayjs(end, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).endOf("day");
        }

        else {
            calculatedStartDate = dayjs(selections.timePeriod ? selections.timePeriod : value, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).startOf("day");
            calculatedEndDate = dayjs(selections.timePeriod ? selections.timePeriod : value, 'DD-MM-YYYY').tz(CommonUtil.getTimeZoneId()).endOf("day");
        }

        if (!calculatedStartDate?.isValid() || !calculatedEndDate?.isValid()) {
            toast.error(t("RAL1006"));
            return;
        }

        //  Create a new object `selectValueParameter` based on the `value` parameter
        let selectValueParameter = {}
        if (value.includes("words")) {
            const count = parseInt(value.split(" ")[0]);
            selectValueParameter = {
                ...selections,
                length: !isNaN(count) ? count : 0
            }
        } else {
            selectValueParameter = {
                ...selections
            }
        }

        const payload = {
            ...buildPayload(selectValueParameter),
            startTime: calculatedStartDate.utc().format(),
            endTime: calculatedEndDate.utc().format(),
        };

        setGenerateSummary(undefined);
        generateDaySummarize(payload)
            .then((res) => {
                if (res?.data?.code === "RCI0000") {
                    if (res?.data?.data?.summary?.summary) {
                        setCurrentAiLimit(res?.data?.data?.currValue || currentAiLimit + 1);
                        const processedSummary = processTextSummary(res.data?.data?.summary?.summary);
                        setGenerateSummary(processedSummary);
                        setIsGenerated(true);
                        setHasActivityData(true);
                        updateUserPreferences(userId, {
                            ...userPreferences,
                            aiLanguage: selections.language,
                            style: selections.style,
                            wordCount: parseInt(selections.length),
                            timeRange: selections.timePeriod
                        });
                        setTimeout(() => {
                            addStep({
                                id: `generated-summary-${Date.now()}`,
                                type: 'generatedSummary',
                                summary: processedSummary,
                                rawSummary: res.data?.data?.summary?.summary
                            });
                            setLoadingSummary(false);
                        }, 1000);
                        setRefreshed(true);

                    } else {
                        setHasActivityData(false);
                        setLoadingSummary(false);
                        addStep({
                            id: `no-activity-${Date.now()}`,
                            type: 'noActivity',
                            message: t('RAL0038')
                        });
                    }
                } else if (res.data?.code === "RCE0102") {
                    setHasActivityData(false);
                    setDisableAll(false);
                    setLoadingSummary(false);
                    addStep({
                        id: `no-activity-${Date.now()}`,
                        type: 'noActivity',
                        message: t('RAL0038')
                    });
                } else {
                    toast.error(t("RAL0508"));
                    setTimeout(() => {

                        addStep({
                            id: `restart-${Date.now()}`,
                            type: 'question',
                            message: t('RAL1086'),
                            options: [{ label: 'Get Started', action: 'start' }],
                            disabled: false
                        });
                        setCanStartNewFlow(true);


                    }, 5000);
                }
                setLoadingSummary(false);
            })
            .catch((e) => {
                setHasActivityData(false);
                setLoadingSummary(false);
                addStep({
                    id: `no-activity-${Date.now()}`,
                    type: 'noActivity',
                    message: t('RAL0038')
                });
            });
    };

    useEffect(() => {
        const styleElement = document.createElement('style');
        document.head.appendChild(styleElement);
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);
    const getPopupTitle = (subscriptionDetails, t, isRestrictPopup = false) => {
        if (CommonUtil.getStatusCondition(subscriptionDetails)) {
            return isRestrictPopup ? t("RAL0902") : t("RAL0867");
        } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
            return t("RAL0850");
        } else {
            return t("RAL0852");
        }
    };

    const getPopupContent = (subscriptionDetails, t, isRestrictPopup = false) => {
        if (CommonUtil.getStatusCondition(subscriptionDetails)) {
            return isRestrictPopup ? t("RAL0903") : t("RAL0868");
        } else if (CommonUtil.isPlanCodeDefault(subscriptionDetails)) {
            return t("RAL0849");
        } else {
            return t("RAL0851");
        }
    };

    function formatTimeUntilReset() {
        const now = new Date();

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);

        const diff = tomorrow - now;

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        return `${hours} hours ${minutes} minutes`;
    }
    return (
        <Box sx={{ display: isDateSelected ? 'none' : 'block' }} >
            {isLimitLoading ? (
                <CircularProgress size={24} />
            ) : (
                <>
                    <div className='credits-available'>
                        <span className={remainingAttempts === 0 ? 'summarization-history0red' : 'summarization-history11'}>
                            {t('RAL1085')} {remainingAttempts}/{aiLimit}
                        </span>
                    </div>

                    {(remainingAttempts === 0 && !isGenerated) ? (
                        <>
                            <div className='summarization-history111'>
                                <span>{t('RAL1084')}</span>
                                <span>{t('RAL1083')} {formatTimeUntilReset()}</span>
                            </div>
                            <div className='comeback-later'>
                                <span className='summarization-history11'>{t('RAL1082')}</span>
                                <button className="option-label-aitext"
                                    onClick={() => {
                                        if (!["OWNER", "MANAGER"].includes(CommonUtil.getRoleCode())) {
                                            setRestrictPopupOpen(true);
                                        } else {
                                            navigate("/subscription-manage");
                                        }
                                    }}
                                >
                                    {t('RAL0908')}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <Stack spacing={0} className='summarization-history111'>
                                {chatSteps.map((step, index) => (
                                    <React.Fragment key={index}>
                                        <Box key={index} display="flex" justifyContent={step.type === 'user' ? 'flex-end' : 'flex-start'}>
                                            <Box maxWidth="70%">
                                                {step.type === 'question' && (
                                                    <>
                                                        <Typography variant="body1" className='summarization-history11'> {step.message}</Typography>
                                                        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                                                            {step.options.map((opt, idx) => {
                                                                let isSelected = step.selected === opt.value;

                                                                if (opt.value === 'Custom' && step.selected?.includes('-') && !step.selected?.includes('to')) {
                                                                    isSelected = true;
                                                                }

                                                                if (opt.value === 'Custom Week' && step.selected?.includes('to')) {
                                                                    isSelected = true;
                                                                }

                                                                return opt.label === "Get Started" ? (
                                                                    <button
                                                                        key={idx}
                                                                        className={`get-started${step.disabled || step.locked || (disableAll && !(canStartNewFlow && opt.action === 'start')) ? ' disabled' : ''}`}
                                                                        onClick={() => handleAction(opt.action, opt.value)}
                                                                        disabled={step.disabled || step.locked || (disableAll && !(canStartNewFlow && opt.action === 'start'))}
                                                                    >
                                                                        {opt.label}
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        key={idx}
                                                                        className={`option-label-ai${mode === 'dark' ? 'Dark' : ''}${isSelected ? ' selected' : ''}`}
                                                                        onClick={(e) => {
                                                                            if (opt.label === 'Custom') {
                                                                                if (weekPickerRef.current) weekPickerRef.current.closePicker();
                                                                                handleOpenDatePicker(e);
                                                                            } else if (opt.label === 'Custom Week') {
                                                                                if (datePickerRef.current) datePickerRef.current.closePicker();
                                                                                handleOpenWeekPicker(e);
                                                                            } else {
                                                                                if (datePickerRef.current) datePickerRef.current.closePicker();
                                                                                if (weekPickerRef.current) weekPickerRef.current.closePicker();
                                                                                setOpenPickerType(null);
                                                                                handleAction(opt.action, opt.value);
                                                                            }
                                                                        }}
                                                                        disabled={step.disabled || step.locked || (disableAll && !(canStartNewFlow && opt.action === 'start'))}
                                                                    >
                                                                        {opt.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </Stack>

                                                        {step.extraChange && (
                                                            <Box mt={1}>
                                                                <button
                                                                    className="option-label-aitext"
                                                                    onClick={() => handleAction(step.extraChange.action)}
                                                                    disabled={
                                                                        step.locked ||
                                                                        disableAll ||
                                                                        (activeChange && activeChange !== step.extraChange.target)
                                                                    }
                                                                >
                                                                    {step.extraChange.label}
                                                                </button>
                                                            </Box>
                                                        )}
                                                    </>
                                                )}

                                                {step.type === 'user-prefrence' && (
                                                    <>
                                                        <Typography variant="body1" className='summarization-history11'>
                                                            {t('RAL1077')}<br /><br /> {t('RAL1104')}:<br />
                                                            {t('RAL1078')}<span className='summarization-history12'>{" " + userPreferences?.aiLanguage}, </span>
                                                            {t('RAL1079')}<span className='summarization-history12'>{" " + userPreferences?.style}, </span>
                                                            {t('RAL1080')}<span className='summarization-history12'>{" " + userPreferences?.wordCount} {t('RAL1081')}</span>
                                                            <br />
                                                            {t('RAL1076')}
                                                            <br /><br />
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                                                            {step.options.map((opt, idx) => {
                                                                return (
                                                                    <button
                                                                        key={idx}
                                                                        className={`option-label-ai${mode === 'dark' ? 'Dark' : ''}${(userPrefTimeSelection.value === opt.label && savedprefrence) ? ' selected' : ''}`}
                                                                        onClick={(e) => {
                                                                            if (userPrefTimeSelection?.currentStatus !== "activated" && savedprefrence) {
                                                                                SetUserPrefTimeSelection({ type: "user-prefrence-active", value: opt.label, currentStatus: "chosen" })
                                                                                setUpgradeFromat(true);
                                                                                if (opt.label === 'Custom') {
                                                                                    if (weekPickerRef.current) weekPickerRef.current.closePicker();
                                                                                    handleOpenDatePicker(e);
                                                                                } else if (opt.label === 'Custom Week') {
                                                                                    if (datePickerRef.current) datePickerRef.current.closePicker();
                                                                                    handleOpenWeekPicker(e);
                                                                                } else {
                                                                                    if (datePickerRef.current) datePickerRef.current.closePicker();
                                                                                    if (weekPickerRef.current) weekPickerRef.current.closePicker();
                                                                                    setOpenPickerType(null);
                                                                                    handleAction(opt.action, opt.value);
                                                                                }

                                                                            }
                                                                        }}
                                                                        disabled={userPrefTimeSelection?.currentStatus == "activated"}
                                                                    >
                                                                        {opt.label}
                                                                    </button>
                                                                );
                                                            })}
                                                        </Stack>
                                                        <Box mt={1}>
                                                            <Typography variant="body1" className='summarization-history11'>
                                                                {t('RAL1075')}<span className='option-label-aitext-upgrade'
                                                                    onClick={() => userPrefTimeSelection?.currentStatus !== "activated" && handleAction("user-prefrence", "Upgrade Format")}
                                                                >{t('RAL1074')}</span>
                                                            </Typography>
                                                        </Box>
                                                    </>
                                                )}

                                                {step.type === 'requestion' && (
                                                    <>
                                                        <Typography variant="body1" className='step-messegefont1' >{step.message}</Typography>
                                                        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                                                            {step.options.map((opt, idx) => {
                                                                let isSelected = step.selected === opt.value;

                                                                if (opt.value === 'Custom' && step.selected?.includes('-') && !step.selected?.includes('to')) {
                                                                    isSelected = true;
                                                                }

                                                                if (opt.value === 'Custom Week' && step.selected?.includes('to')) {
                                                                    isSelected = true;
                                                                }


                                                                return (
                                                                    <button
                                                                        key={idx}
                                                                        className={`${opt.label === "Get Started" ? 'get-started' : `option-label-ai${mode === 'dark' ? 'Dark' : ''}`}${isSelected ? ' selected' : ''}`}
                                                                        onClick={(e) => {
                                                                            if (opt.label === 'Custom') {
                                                                                if (weekPickerRef.current) weekPickerRef.current.closePicker();
                                                                                handleReOpenDatePicker(e);
                                                                            } else if (opt.label === 'Custom Week') {
                                                                                if (datePickerRef.current) datePickerRef.current.closePicker();
                                                                                handleReOpenWeekPicker(e);
                                                                            } else {
                                                                                if (datePickerRef.current) datePickerRef.current.closePicker();
                                                                                if (weekPickerRef.current) weekPickerRef.current.closePicker();
                                                                                setOpenPickerType(null);
                                                                                handleAction(opt.action, opt.value);
                                                                            }
                                                                        }}
                                                                        disabled={step.disabled || step.locked || (disableAll && !(canStartNewFlow && opt.action === 'start'))}
                                                                    >
                                                                        {opt.label}
                                                                    </button>
                                                                )
                                                            })}
                                                        </Stack>
                                                    </>
                                                )}

                                                {step.type === 'user' && (
                                                    <Chip label={step.message} className={mode === "dark" ? 'step-messegefont11Dark' : 'step-messegefont11'} />
                                                )}

                                                {step.type === 'noActivity' && !loadingSummary && !hasActivityData && (
                                                    <Box className="no-activity-message">

                                                        <Typography variant="body2" className={mode === "dark" ? "step-messegefont1Dark" : "step-messegefont1"}>
                                                            {t('RAL1071')}<br />
                                                            {t('RAL1072')}<br />
                                                            {t('RAL1073')}
                                                        </Typography>
                                                        <Stack direction="row">
                                                            <button
                                                                className="option-label-aitext"
                                                                onClick={() => handleAction('popTimePeriod')}
                                                            // disabled={disableAll}
                                                            >
                                                                {t('RAL1070')}
                                                            </button>

                                                        </Stack>
                                                    </Box>
                                                )}


                                                {step.type === 'summary' && (
                                                    <Box mt={0}>
                                                        <Typography variant="body1" className='step-messegefont1' >
                                                            {t("RAL1069") + ":"}
                                                        </Typography>
                                                        <Stack spacing={-1} mt={2}>
                                                            {['language', 'timePeriod', 'style', 'length'].map((field) => (
                                                                <Stack key={field} direction="row" spacing={0} alignItems="center">
                                                                    <Typography variant='body1' className='step-messegefont1'>• {`${field.charAt(0).toUpperCase() + field.slice(1)}: ${step.summarySelections?.[field]}`}</Typography>
                                                                    <button
                                                                        className="option-label-aitext1"
                                                                        onClick={() => handleAction(`change${field.charAt(0).toUpperCase() + field.slice(1)}`)}
                                                                        disabled={step.locked || disableAll}
                                                                    >
                                                                        {t('RAL1068')}
                                                                    </button>
                                                                </Stack>
                                                            ))}
                                                        </Stack>

                                                        <button
                                                            className="get-started"
                                                            onClick={() => {
                                                                addStep({
                                                                    id: `loader-${Date.now()}`,
                                                                    type: 'loader',
                                                                });
                                                                handleGenerate();
                                                            }}
                                                            disabled={step.locked || disableAll}
                                                        >
                                                            {t('RAL0353')}
                                                        </button>


                                                    </Box>
                                                )}

                                                {step.type === 'generatedSummary' && (
                                                    <Box mt={2} justifyItems={'start'}>
                                                        <Box
                                                            ref={el => (summaryRef.current[step.id] = el)}
                                                            className={mode === "dark" ? "type-writer-space-dark" : "type-writer-space"}
                                                            dangerouslySetInnerHTML={{ __html: step.summary }}
                                                        />
                                                        {isSummaryTyped && (
                                                            <CopyAndExport textToCopy={step.rawSummary} />
                                                        )}
                                                    </Box>
                                                )}

                                                {step.type === 'loader' && (
                                                    <>
                                                        {loadingSummary && (
                                                            <Box mt={1}>
                                                                <Typography mt={1} variant="body1" className='summarization-history11'>
                                                                    {t('RAL1105')}
                                                                </Typography>
                                                                <CircularProgress size={24} />
                                                            </Box>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Box>

                                        {(step.type === 'user' || step.type === 'generatedSummary') && (
                                            <div
                                                className={
                                                    mode === "dark" ? "dividerbillingcarddark1" : "dividerbillingcard1"
                                                }
                                            ></div>
                                        )}
                                    </React.Fragment>
                                ))}
                                <div ref={chatEndRef} />
                                <CustomizedButtonDatePicker
                                    ref={datePickerRef}
                                    label="Select a Date"
                                    onChange={(date) => {
                                        handleDateSelection(date);
                                    }}
                                    onOpenChange={(isOpen) => {
                                        if (!isOpen && openPickerType === 'date') {
                                            setOpenPickerType(null);
                                        }
                                    }}
                                    onClosePop={() => SetUserPrefTimeSelection({ type: savedprefrence ? "user-prefrence-active" : "user-prefrence-inactive", value: "", currentStatus: "chosen" })}
                                />
                                <CustomizedButtonWeekPicker
                                    ref={weekPickerRef}
                                    label="Select a Week"
                                    onChange={(date) => {
                                        handleWeekSelection(date);
                                    }}
                                    onOpenChange={(isOpen) => {
                                        if (!isOpen && openPickerType === 'week') {
                                            setOpenPickerType(null);
                                        }
                                    }}
                                    onClosePop={() => SetUserPrefTimeSelection({ type: savedprefrence ? "user-prefrence-active" : "user-prefrence-inactive", value: "", currentStatus: "chosen" })}
                                />

                                <CustomizedButtonDatePicker
                                    ref={redatePickerRef}
                                    label="Select a Date"
                                    onChange={(date) => {
                                        if (date)
                                            handleReDateSelection(date);
                                    }}
                                />
                                <CustomizedButtonWeekPicker
                                    reset={resetTimeValue}
                                    ref={reweekPickerRef}
                                    label="Select a Week"
                                    onChange={(date) => {
                                        if (date)
                                            handleReWeekSelection(date);
                                    }}
                                />
                            </Stack>
                        </>
                    )}

                    {["OWNER", "MANAGER"].includes(CommonUtil.getRoleCode()) && (
                        <UpgradePopup
                            open={upgradePopupOpen}
                            onClose={() => setUpgradePopupOpen(false)}
                            onConfirm={() => {
                                if (CommonUtil.getRoleCode() === "OWNER" || CommonUtil.getRoleCode() === "MANAGER") {
                                    navigate("/subscription-manage");
                                } else {
                                    navigate("/page-not-found");
                                }
                            }}
                            title={getPopupTitle(subscriptionDetails, t)}
                            content={getPopupContent(subscriptionDetails, t)}
                            cancelText={t("RAL0177")}
                            confirmText={t("RAL0848")}
                        />
                    )}

                    {!["OWNER"].includes(CommonUtil.getRoleCode()) && (
                        <RestrictPermissionPopup
                            open={restrictPopupOpen}
                            onClose={() => setRestrictPopupOpen(false)}
                            title={getPopupTitle(subscriptionDetails, t, true)}
                            content={getPopupContent(subscriptionDetails, t, true)}
                            cancelText={t("RAL0177")}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default Chat;
